import { useState } from 'react';
import { Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FuseLoading from '@fuse/core/FuseLoading';
import SimpleDialog from '@fuse/core/SimpleDialog';
import {
  useGetAsTreeViewPermissionsQuery,
  useInsertPermissionMutation,
} from 'src/app/services/permissionApi';
import PermissionForm from './permisstionForm';
import handleError from '@fuse/core/errorHandler';

const PermissionNodeComponent = ({ node, depth = 0, parentChain = [], onAddClick }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={`ml-${depth * 4} mt-2`}>
      <div
        className="flex items-center justify-between p-5 cursor-pointer border-b"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex-1 flex items-center justify-between">
          <div className="">
            <p className="font-bold mb-3">{node.name}</p>
            <p className="">{node.target}</p>
          </div>
          {node.target !== 'Functionality' && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation();
                onAddClick(node, parentChain);
              }}
            >
              {t('Add')}
            </Button>
          )}
        </div>
      </div>

      {isExpanded &&
        node.childIds?.length > 0 &&
        node.childIds.map((child) => (
          <div key={child.id} className="ml-6 border-l pl-3">
            <PermissionNodeComponent
              node={child}
              depth={depth + 1}
              parentChain={[...parentChain, node]}
              onAddClick={onAddClick}
            />
          </div>
        ))}
    </div>
  );
};

const Permission = () => {
  const { isDarkMode } = useSelector((state) => state.themeSlice);
  const { t } = useTranslation('translations');

  const { data: treeViewPermission, isLoading, refetch } = useGetAsTreeViewPermissionsQuery();
  const [insetPermission] = useInsertPermissionMutation();
  const [localTree, setLocalTree] = useState(null);

  const [selectedNode, setSelectedNode] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (isLoading) return <FuseLoading />;

  if (!localTree && treeViewPermission) {
    setLocalTree(JSON.parse(JSON.stringify(treeViewPermission)));
  }

  const handleAddClick = (node, parentChain) => {
    setSelectedNode({ node, parentChain });
    setIsDialogOpen(true);
  };

  const addChildToNode = (nodes, nodeId, child) => {
    try {
      for (let n of nodes) {
        if (n.id === nodeId) {
          n.childIds.push(child);
          return true;
        }
        if (n.childIds?.length > 0) {
          if (addChildToNode(n.childIds, nodeId, child)) return true;
        }
      }
      return false;
    } catch (error) {
      handleError(error);
    }
  };

  const handleCreate = async (newData) => {
    try {
      const updatedTree = [...localTree];

      addChildToNode(updatedTree, selectedNode?.node?.id, newData);

      setLocalTree(updatedTree);
      setIsDialogOpen(false);
      setSelectedNode(null);
      const payload = updatedTree.length ? [...updatedTree] : newData;
      await insetPermission(...payload).unwrap();
      refetch();
    } catch (error) {
      handleError(error);
    }
  };

  if (!localTree) return null;

  return (
    <div style={{ display: 'grid' }}>
      <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
        <h2 className="ml-5">{t('Permission')}</h2>
      </div>
      {!localTree?.length && (
        <div className="dark:bg-dark flex items-center justify-center py-10">
          <Button
            onClick={() => setIsDialogOpen(true)}
            sx={{ width: '220px' }}
            size="small"
            variant="outlined"
            color="secondary"
          >
            {t('AddPermission')}
          </Button>
        </div>
      )}
      <Paper variant="outlined" className="mt-2 p-2">
        {localTree &&
          localTree.map((node) => (
            <PermissionNodeComponent key={node.id} node={node} onAddClick={handleAddClick} />
          ))}
      </Paper>
      <SimpleDialog title={t('Adding')} open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        <PermissionForm onCreate={handleCreate} permission={[]} />
      </SimpleDialog>
    </div>
  );
};

export default Permission;
